import { createMemo, type Accessor } from 'solid-js';
import { useQuery, type UseQueryResult } from '@tanstack/solid-query';
import type {
  GeospatialLogger,
  MapLayer,
  MapWorkspace,
} from '@trimble/geospatial';
import type { GeovectorRegion } from '@trimble/utilities';
import { filterLoadedWorkspaceLayers } from './filter-loaded-workspace-layers';
import type { FeatureExtractionRepositories } from './feature-extraction-repositories';

export interface ProjectLayersData {
  workspaces: MapWorkspace[];
  layers: MapLayer[];
  defaultLayerId: string | undefined;
}

export interface FeatureExtractionFetchKey {
  readonly projectId: string;
  readonly region: GeovectorRegion;
}

export interface CreateFeatureExtractionLoadersParams {
  readonly isFeatureExtractionEnabled: Accessor<boolean>;
  readonly selectedProjectId: Accessor<string | undefined>;
  readonly authToken: Accessor<string | undefined>;
  readonly mapsRegion: Accessor<GeovectorRegion | undefined>;
  readonly repositories: Accessor<FeatureExtractionRepositories | undefined>;
  readonly logger: GeospatialLogger;
}

export interface FeatureExtractionLoaders {
  readonly layersFetchKey: Accessor<FeatureExtractionFetchKey | undefined>;
  readonly layersQuery: UseQueryResult<ProjectLayersData, Error>;
}

export async function loadProjectLayers(
  projectId: string,
  { layerRepository }: FeatureExtractionRepositories,
  logger: GeospatialLogger
): Promise<ProjectLayersData> {
  let workspaces: MapWorkspace[] = [];
  let layers: MapLayer[] = [];
  let defaultLayerId: string | undefined;

  try {
    const [loadedWorkspaces, loadedLayers] = await Promise.all([
      layerRepository.listWorkspaces(projectId),
      layerRepository.listLayers(projectId),
    ]);

    layers = await Promise.all(
      loadedLayers.map(async (layer) => {
        try {
          const template = await layerRepository.getLatestTemplate(
            projectId,
            layer
          );
          return template ? { ...layer, template } : layer;
        } catch (error) {
          logger.warn(
            'Failed to load template for layer',
            { layerId: layer.id },
            error instanceof Error ? error : new Error(String(error))
          );
          return layer;
        }
      })
    );

    workspaces = filterLoadedWorkspaceLayers(loadedWorkspaces, layers, logger);

    const defaultLayer = layers.find((layer) => !layer.isSystem) ?? layers[0];
    defaultLayerId = defaultLayer?.id;
  } catch (error) {
    logger.error(
      'Failed to load project feature layers',
      { projectId },
      error instanceof Error ? error : new Error(String(error))
    );
    throw error;
  }

  return { workspaces, layers, defaultLayerId };
}

export function createFeatureExtractionLoaders(
  params: CreateFeatureExtractionLoadersParams
): FeatureExtractionLoaders {
  const {
    isFeatureExtractionEnabled,
    selectedProjectId,
    authToken,
    mapsRegion,
    repositories,
    logger,
  } = params;

  function createFeatureExtractionQuery<T>(
    queryKeyPrefix: string,
    fetchKey: Accessor<FeatureExtractionFetchKey | undefined>,
    load: (
      projectId: string,
      repositories: FeatureExtractionRepositories
    ) => Promise<T>,
    empty: T
  ): UseQueryResult<T, Error> {
    return useQuery(() => {
      const key = fetchKey();
      const enabled = key !== undefined;

      return {
        enabled,
        queryKey: enabled
          ? [queryKeyPrefix, key.projectId, key.region]
          : ([queryKeyPrefix, 'disabled'] as const),
        queryFn: async () => {
          if (key === undefined) {
            return empty;
          }
          const repos = repositories();
          if (repos === undefined) {
            return empty;
          }
          return load(key.projectId, repos);
        },
        staleTime: 0,
      };
    });
  }

  const layersFetchKey = createMemo<FeatureExtractionFetchKey | undefined>(
    () => {
      const projectId = selectedProjectId();
      const token = authToken();
      const region = mapsRegion();
      if (
        !isFeatureExtractionEnabled() ||
        !projectId ||
        !token ||
        region === undefined
      ) {
        return undefined;
      }
      return { projectId, region };
    }
  );

  // eslint-disable-next-line solid/reactivity -- fetchKey() is read inside useQuery's options factory; the rule cannot see through createFeatureExtractionQuery
  const layersQuery = createFeatureExtractionQuery(
    'feature-extraction-layers',
    layersFetchKey,
    (projectId, repos) => loadProjectLayers(projectId, repos, logger),
    { workspaces: [], layers: [], defaultLayerId: undefined }
  );

  return {
    layersFetchKey,
    layersQuery,
  };
}
